import React, { useState } from 'react';
import { RiasecLastTestResponse, RiasecTestData, RiasecType, RiasecCategoryInfo, RiasecProfession } from '../../typings/riasec';
import RadarChart from '../RadarChart';
import RiasecPdfDownload from './RiasecPdfDownload';
import Button from '../UI/Button';
import { Card, CardContent, CardHeader } from "../UI/Card";
import { Eye } from "lucide-react";

interface RiasecLastTestProps {
  lastTest: RiasecLastTestResponse;
  onViewDetail: () => void;
  professions?: RiasecProfession[];
}

const RIASEC_CATEGORIES: RiasecCategoryInfo[] = [
  { type: 'R', name: 'Realistic', title: 'Realistic (Doer)', description: 'Suka bekerja dengan tangan, alat, dan mesin', color: '#FF6384' },
  { type: 'I', name: 'Investigative', title: 'Investigative (Thinker)', description: 'Suka menganalisis, meneliti, dan memecahkan masalah', color: '#36A2EB' },
  { type: 'A', name: 'Artistic', title: 'Artistic (Creator)', description: 'Suka berkreasi, berimajinasi, dan berekspresi', color: '#FFCE56' },
  { type: 'S', name: 'Social', title: 'Social (Helper)', description: 'Suka membantu, mengajar, dan berinteraksi dengan orang', color: '#4BC0C0' },
  { type: 'E', name: 'Enterprising', title: 'Enterprising (Persuader)', description: 'Suka memimpin, menjual, dan mempengaruhi orang lain', color: '#9966FF' },
  { type: 'C', name: 'Conventional', title: 'Conventional (Organizer)', description: 'Suka mengorganisir, mengatur data, dan mengikuti prosedur', color: '#FF9F40' }
];

const RiasecLastTest: React.FC<RiasecLastTestProps> = ({ lastTest, onViewDetail, professions = [] }) => {
  const [expandedCategories, setExpandedCategories] = useState<{ [key: string]: boolean }>({});

  const getRadarData = (testData: RiasecTestData) => {
    return {
      labels: ['Realistic', 'Investigative', 'Artistic', 'Social', 'Enterprising', 'Conventional'],
      datasets: [
        {
          label: 'IDE Score',
          data: [testData.r, testData.i, testData.a, testData.s, testData.e, testData.c],
          backgroundColor: 'rgba(54, 162, 235, 0.2)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 2,
          pointBackgroundColor: 'rgba(54, 162, 235, 1)',
        },
      ],
    };
  };

  const getProfessionsByType = (type: RiasecType) => {
    return professions.filter(profession => profession.type === type);
  };

  const toggleExpanded = (categoryType: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryType]: !prev[categoryType]
    }));
  };

  const getTop3Categories = () => {
    return RIASEC_CATEGORIES
      .map(category => ({
        ...category,
        score: lastTest.data.test[category.type.toLowerCase() as keyof RiasecTestData] as number
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);
  };

  return (
    <Card className="mt-4">
      <CardHeader className="border-b border-b-muted">
        <h5 className="text-lg font-semibold">Tes IDE Terakhir</h5>
      </CardHeader>
      <CardContent className="p-4">
        <div>
          <p className="text-gray-500 text-xs flex justify-end mb-3">
            Tanggal Tes:{" "}
            {new Date(lastTest.data.test.created_at).toLocaleDateString(
              "id-ID"
            )}
          </p>
          <div className="flex flex-col md:flex-row md:flex-wrap">
            <div className="md:w-full mb-3">
              <h6 className="text-center font-semibold mb-3">Grafik IDE</h6>
              <div style={{ height: "250px" }}>
                <RadarChart data={getRadarData(lastTest.data.test)} />
              </div>
            </div>

            <div className="md:w-full">
              <h6 className="text-center font-semibold mb-3">
                Top 3 Skor IDE
              </h6>
              <div className="riasec-scores">
                {getTop3Categories().map((category, index) => {
                  const categoryProfessions = getProfessionsByType(
                    category.type
                  );
                  const isExpanded = expandedCategories[category.type];
                  const displayProfessions = isExpanded
                    ? categoryProfessions
                    : categoryProfessions.slice(0, 4);
                  const hasMore = categoryProfessions.length > 4;

                  return (
                    <div key={category.type} className="mb-3 pb-2 border-b">
                      <div className="flex justify-between items-center mb-1">
                        <span style={{ color: category.color }}>
                          <strong>
                            #{index + 1} {category.title}
                          </strong>
                        </span>
                        <span
                          className="inline-block px-2 py-1 text-xs font-medium rounded"
                          style={{
                            backgroundColor: category.color,
                            color: "#fff",
                            fontWeight: "bold",
                          }}
                        >
                          {category.score}
                        </span>
                      </div>
                      <small className="text-gray-500 block mb-2">
                        {category.description}
                      </small>

                      {categoryProfessions.length > 0 && (
                        <div className="mt-2">
                          <small className="text-gray-500">
                            Profesi yang cocok:
                          </small>
                          <div className="mt-1">
                            <small>
                              {displayProfessions.map((prof, idx) => (
                                <span key={prof.id}>
                                  {prof.profession}
                                  {idx < displayProfessions.length - 1 && ", "}
                                </span>
                              ))}
                              {!isExpanded && hasMore && (
                                <span>
                                  ...{" "}
                                  <Button
                                    link
                                    size="sm"
                                    className="p-0 ml-1"
                                    style={{
                                      fontSize: "0.875rem",
                                      verticalAlign: "baseline",
                                    }}
                                    onClick={() =>
                                      toggleExpanded(category.type)
                                    }
                                  >
                                    Lihat lebih banyak
                                  </Button>
                                </span>
                              )}
                              {isExpanded && hasMore && (
                                <span>
                                  {" "}
                                  <Button
                                    link
                                    size="sm"
                                    className="p-0 ml-1"
                                    style={{
                                      fontSize: "0.875rem",
                                      verticalAlign: "baseline",
                                    }}
                                    onClick={() =>
                                      toggleExpanded(category.type)
                                    }
                                  >
                                    Lihat lebih sedikit
                                  </Button>
                                </span>
                              )}
                            </small>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex flex-col mt-3">
            <Button primary className="mb-2 gradient-primary" onClick={onViewDetail}>
              <Eye /> Lihat Detail Tes
            </Button>
            <RiasecPdfDownload testId={lastTest.data.test.id} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default RiasecLastTest;
